import React, { useState } from 'react';
import { useStudio, PANEL_COLORS, COLOR_GROUPS } from '@/lib/store';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LockKeyhole } from 'lucide-react';

const MODES = [
  { value: 'none', label: 'None', hint: 'Drags move only one vertex' },
  { value: 'antipodal', label: 'Antipodal', hint: 'Mirror through the ball center' },
  { value: 'mirrorX', label: 'Mirror X', hint: 'Flip across the vertical (YZ) plane' },
  { value: 'mirrorY', label: 'Mirror Y', hint: 'Flip across the horizontal (XZ) plane' },
  { value: 'mirrorZ', label: 'Mirror Z', hint: 'Flip across the front–back (XY) plane' },
  { value: 'ring', label: 'Ring rotation', hint: 'N-fold around the dragged point' },
  { value: 'full', label: 'Full symmetry', hint: 'Every equivalent vertex follows' },
];

function Segmented({ options, value, onChange, testId }) {
  return (
    <div className="segmented w-full" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o.value}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          data-testid={`${testId}-${o.value}`}
          className="flex-1"
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function RightInspector() {
  const seamWidth = useStudio((s) => s.seamWidth);
  const setSeamWidth = useStudio((s) => s.setSeamWidth);
  const finish = useStudio((s) => s.finish);
  const setFinish = useStudio((s) => s.setFinish);
  const wireframe = useStudio((s) => s.wireframe);
  const setWireframe = useStudio((s) => s.setWireframe);
  const showHandles = useStudio((s) => s.showHandles);
  const setShowHandles = useStudio((s) => s.setShowHandles);
  const symmetryMode = useStudio((s) => s.symmetryMode);
  const setSymmetryMode = useStudio((s) => s.setSymmetryMode);
  const stabilizer = useStudio((s) => s.stabilizer);
  const selectedPanel = useStudio((s) => s.selectedPanel);
  const setSelectedPanel = useStudio((s) => s.setSelectedPanel);
  const colors = useStudio((s) => s.colors);
  const faces = useStudio((s) => s.faces);
  const setPanelColor = useStudio((s) => s.setPanelColor);
  const setAllColors = useStudio((s) => s.setAllColors);
  const setRainbow = useStudio((s) => s.setRainbow);

  return (
    <div className="w-full flex flex-col p-5 gap-6 overflow-y-auto" data-testid="right-inspector-content">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-3">
          Surface
        </div>
        <Label className="text-xs text-slate-500 mb-2 block">Finish</Label>
        <Segmented
          testId="finish-segmented"
          value={finish}
          onChange={setFinish}
          options={[
            { value: 'matte', label: 'Matte' },
            { value: 'gloss', label: 'Gloss' },
            { value: 'leather', label: 'Leather' },
          ]}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-slate-500">Seam width</Label>
          <span className="font-mono text-[11px] text-slate-500" data-testid="seam-width-value">
            {seamWidth.toFixed(3)}
          </span>
        </div>
        <Slider
          value={[seamWidth]}
          min={0}
          max={0.1}
          step={0.005}
          onValueChange={(v) => setSeamWidth(v[0])}
          data-testid="slider-seam-width"
        />
      </div>

      <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
        <Label className="text-sm text-slate-700">Show vertex handles</Label>
        <Switch checked={showHandles} onCheckedChange={setShowHandles} data-testid="switch-handles" />
      </div>
      <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 -mt-3">
        <Label className="text-sm text-slate-700">Wireframe</Label>
        <Switch checked={wireframe} onCheckedChange={setWireframe} data-testid="switch-wireframe" />
      </div>
      <div className={`rounded-xl border px-3 py-3 -mt-3 ${symmetryMode !== 'none' ? 'bg-blue-50/60 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
        <Label className="text-sm text-slate-800 flex items-center gap-1.5 mb-1">
          <LockKeyhole className="w-3.5 h-3.5" />
          Symmetry mode
        </Label>
        <Select value={symmetryMode} onValueChange={setSymmetryMode}>
          <SelectTrigger className="rounded-lg h-9 bg-white" data-testid="select-symmetry-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODES.map((m) => (
              <SelectItem key={m.value} value={m.value} data-testid={`symmetry-mode-${m.value}`}>
                <div className="flex flex-col">
                  <span className="text-sm">
                    {m.label}
                    {m.value === 'ring' && stabilizer && stabilizer.length > 1 && (
                      <span className="ml-1.5 font-mono text-[10px] text-slate-500">
                        {stabilizer.length}-fold
                      </span>
                    )}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-[11px] text-slate-500 mt-1.5">
          {MODES.find((m) => m.value === symmetryMode)?.hint}
          {symmetryMode === 'ring' && (!stabilizer || stabilizer.length <= 1) && (
            <span className="block text-amber-600 mt-1">No rotational axis on this template.</span>
          )}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-3">
          Colors
        </div>
        <div className="text-xs text-slate-500 mb-2">Quick two-tone</div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button
            className="rounded-xl border border-slate-200 h-10 text-xs font-medium bg-white hover:bg-slate-50"
            onClick={() => setAllColors('#111827', '#F9FAFB')}
            data-testid="btn-preset-classic"
          >
            Classic
          </button>
          <button
            className="rounded-xl border border-slate-200 h-10 text-xs font-medium bg-white hover:bg-slate-50"
            onClick={() => setAllColors('#007AFF', '#F9FAFB')}
            data-testid="btn-preset-kinetic"
          >
            Kinetic
          </button>
          <button
            className="rounded-xl border border-slate-200 h-10 text-xs font-medium bg-white hover:bg-slate-50"
            onClick={() => setAllColors('#FF3B30', '#111827')}
            data-testid="btn-preset-blaze"
          >
            Blaze
          </button>
          <button
            className="rounded-xl border border-slate-200 h-10 text-xs font-medium bg-white hover:bg-slate-50"
            onClick={() => setAllColors('#CCFF00', '#0F172A')}
            data-testid="btn-preset-volt"
          >
            Volt
          </button>
          <button
            className="col-span-2 rounded-xl border border-slate-200 h-10 text-xs font-medium bg-gradient-to-r from-rose-200 via-amber-200 via-emerald-200 via-sky-200 to-violet-200 hover:opacity-90"
            onClick={setRainbow}
            data-testid="btn-preset-rainbow"
          >
            Rainbow
          </button>
        </div>

        <div className="text-xs text-slate-500 mb-2">
          {selectedPanel !== null ? (
            <>Panel <span className="font-mono">#{selectedPanel + 1}</span> · {faces[selectedPanel]?.length}-gon</>
          ) : (
            'Click a panel on the ball to color it individually'
          )}
        </div>
        <ColorPicker
          disabled={selectedPanel === null}
          value={selectedPanel !== null ? colors[selectedPanel] : null}
          onPick={(c) => setPanelColor(selectedPanel, c)}
        />
        {selectedPanel !== null && (
          <button
            onClick={() => setSelectedPanel(null)}
            className="text-xs text-slate-500 mt-3 hover:text-slate-800"
            data-testid="btn-deselect-panel"
          >
            Deselect panel
          </button>
        )}
      </div>
    </div>
  );
}

function ColorPicker({ disabled, value, onPick }) {
  const groups = Object.keys(COLOR_GROUPS);
  const [tab, setTab] = useState(groups[0]);
  const [hex, setHex] = useState('');
  const isValidHex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex.trim());
  return (
    <div data-testid="color-picker">
      <div className="flex gap-1 mb-3" data-testid="color-tabs">
        {groups.map((g) => (
          <button
            key={g}
            onClick={() => setTab(g)}
            data-testid={`color-tab-${g.toLowerCase()}`}
            className={`flex-1 h-7 text-[11px] font-medium rounded-md border transition-colors ${
              tab === g
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {g}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {COLOR_GROUPS[tab].map((c) => (
          <button
            key={c}
            disabled={disabled}
            onClick={() => onPick(c)}
            data-testid={`color-swatch-${c.replace('#', '')}`}
            className={`w-8 h-8 rounded-full border-2 transition-transform duration-150 ${
              !disabled && value === c
                ? 'border-[#007AFF] scale-110'
                : 'border-white shadow-sm hover:scale-110'
            } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2" data-testid="color-hex-input-row">
        <span className="text-[11px] text-slate-500 font-medium">Hex</span>
        <input
          type="text"
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          disabled={disabled}
          placeholder="#RRGGBB"
          data-testid="color-hex-input"
          className="flex-1 h-7 px-2 text-[11px] font-mono rounded-md border border-slate-200 focus:border-slate-400 outline-none disabled:opacity-40"
        />
        <button
          disabled={disabled || !isValidHex}
          onClick={() => onPick(hex.trim())}
          data-testid="color-hex-apply"
          className="h-7 px-3 text-[11px] font-medium rounded-md bg-slate-900 text-white disabled:opacity-30"
        >
          Apply
        </button>
      </div>
    </div>
  );
}
